import { formatMessagePreview } from "../utils/vcard";
import { useCallback, useEffect, useRef, useState, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import useSound from "use-sound";
import api from "../services/api";
import alertSound from "../assets/sound.mp3";
import chatSound from "../assets/chat_notify.mp3";
import { TicketsContext } from "../context/Tickets/TicketsContext";
import { isChatParticipant } from "../pages/Chat/chatNotifications";

export const categories = ["tickets", "announcements", "internalChats"];
const emptyList = () => ({ items: [], loading: false, loaded: false, error: false, hasMore: false, nextCursor: null });
const requestOptions = { timeout: 20000 };
export default function useNotificationCenter({ user, socket, volume }) {
  const userId = user?.id, companyId = user?.companyId;
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(false);
  const [lists, setLists] = useState(() => Object.fromEntries(categories.map(key => [key, emptyList()])));
  const listsRef = useRef(lists); listsRef.current = lists;
  const active = useRef(null), readFilter = useRef(false), mounted = useRef(true);
  const requests = useRef({}), debounce = useRef(null), summaryRef = useRef(null);
  const history = useHistory(), location = useLocation();
  const path = useRef(location.pathname); path.current = location.pathname;
  const tickets = useContext(TicketsContext);
  const effects = useRef({});
  const [playTicket] = useSound(alertSound, { volume: Number(volume) || 0 });
  const [playChat] = useSound(chatSound, { volume: Number(volume) || 0 });
  effects.current = { playTicket, playChat, tickets, user };

  // Queue refreshes without starving valid responses during continuous incoming traffic.
  // Only identity/filter changes and read/removal events supersede an in-flight snapshot.
  const run = useCallback((key, job, supersede = false) => {
    const slot = requests.current[key] || (requests.current[key] = { version: 0, running: false });
    if (supersede || !slot.running) slot.version += 1;
    slot.next = job;
    if (slot.running) return;
    const drain = async () => {
      slot.running = true;
      while (mounted.current && slot.next) {
        const work = slot.next, version = slot.version; slot.next = null;
        const current = () => mounted.current && version === slot.version;
        await work(current);
      }
      slot.running = false;
    };
    drain();
  }, []);
  const refreshSummary = useCallback(() => {
    if (!userId || !companyId) return;
    run("summary", async current => {
      try {
        const { data } = await api.get("/notifications/summary", requestOptions);
        if (current()) { summaryRef.current = data; setSummary(data); setSummaryError(false); }
      } catch (_) { if (current()) setSummaryError(true); }
    });
  }, [run, userId, companyId]);
  const load = useCallback((category, append = false, supersede = false) => {
    if (!categories.includes(category)) return;
    const state = listsRef.current[category];
    if (append && (state.loading || requests.current[category]?.running || !state.hasMore)) return;
    const cursor = append ? state.nextCursor : undefined, includeRead = readFilter.current;
    setLists(old => ({ ...old, [category]: { ...old[category], loading: true, error: false } }));
    run(category, async current => {
      if (current()) setLists(old => ({ ...old, [category]: { ...old[category], loading: true, error: false } }));
      try {
        const { data } = await api.get("/notifications/items", { ...requestOptions, params: { category, cursor, includeRead: category === "announcements" && includeRead } });
        if (current()) setLists(old => {
          const records = append ? [...old[category].items, ...data.items] : data.items;
          return { ...old, [category]: { items: [...new Map(records.map(item => [item.key, item])).values()], loading: false, loaded: true, error: false, hasMore: data.hasMore, nextCursor: data.nextCursor } };
        });
      } catch (_) { if (current()) setLists(old => ({ ...old, [category]: { ...old[category], loading: false, error: true } })); }
    }, supersede);
  }, [run]);
  const refresh = useCallback(() => {
    refreshSummary();
    if (active.current) load(active.current);
  }, [refreshSummary, load]);
  const activate = useCallback(category => {
    active.current = category;
    if (category) { load(category, false, true); refreshSummary(); }
  }, [load, refreshSummary]);
  const changeReadFilter = useCallback(value => { readFilter.current = value; load("announcements", false, true); }, [load]);
  const invalidate = useCallback((strict = true) => {
    if (strict) Object.values(requests.current).forEach(slot => { slot.version += 1; });
    if (!debounce.current) debounce.current = setTimeout(() => { debounce.current = null; refresh(); }, 250);
  }, [refresh]);
  const markRead = useCallback(async (id, version) => {
    await api.put(`/notifications/announcements/${id}/read`, { version }, requestOptions);
    if (mounted.current) { invalidate(); }
  }, [invalidate]);

  useEffect(() => {
    mounted.current = true;
    refreshSummary();
    const visibleRefresh = () => { if (document.visibilityState !== "hidden") refresh(); };
    window.addEventListener("focus", visibleRefresh);
    document.addEventListener("visibilitychange", visibleRefresh);
    const timer = setInterval(visibleRefresh, 60000);
    return () => {
      mounted.current = false; clearInterval(timer); clearTimeout(debounce.current);
      window.removeEventListener("focus", visibleRefresh);
      document.removeEventListener("visibilitychange", visibleRefresh);
    };
  }, [refreshSummary, refresh]);

  useEffect(() => {
    if (!userId || typeof socket?.on !== "function") return undefined;
    const seen = new Set(), desktop = new Map();
    const startedAt = Date.now();
    const fresh = (kind, message) => {
      if (!message?.id) return false;
      const key = `${kind}:${message.id}`;
      if (seen.has(key)) return false;
      seen.add(key); if (seen.size > 5000) seen.delete(seen.values().next().value);
      const time = Date.parse(message.createdAt);
      return !Number.isFinite(time) || time >= startedAt - 5000;
    };
    const notify = (kind, record, message, title) => {
      const url = kind === "ticket" ? `/tickets/${record.uuid}` : `/chats/${record.uuid}`;
      if (!record.uuid || (path.current === url && document.visibilityState === "visible")) return;
      try { const play = kind === "ticket" ? effects.current.playTicket : effects.current.playChat; if (typeof play === "function") play(); } catch (_) {}
      if (typeof window.Notification !== "function" || window.Notification.permission !== "granted") return;
      try {
        const key = `${kind}:${record.id}`;
        if (desktop.has(key)) desktop.get(key).close();
        const notification = new window.Notification(title, { body: formatMessagePreview(message.body || message.message || "Nova mensagem", message.mediaType).slice(0, 220), icon: kind === "ticket" ? record.contact?.urlPicture || record.contact?.profilePicUrl : undefined, tag: key });
        desktop.set(key, notification);
        notification.onclick = () => { window.focus(); if (kind === "ticket" && effects.current.tickets) effects.current.tickets.setTabOpen(record.status); history.push(url); notification.close(); };
      } catch (_) {}
    };
    const onMessage = data => {
      invalidate(data?.action !== "create");
      const t = data?.ticket, m = data?.message, policy = summaryRef.current?.ticketPolicy;
      if (data?.action !== "create" || !t || !policy || m?.fromMe || m?.read) return;
      if (t.companyId && Number(t.companyId) !== Number(companyId)) return;
      if (t.userId && Number(t.userId) !== Number(userId)) return;
      const inQueue = policy.queueIds.map(Number).includes(Number(t.queueId));
      const sharedScope = (t.status === "pending" && policy.allowPending) || t.status === "group";
      const eligible = (Number(t.userId) === Number(userId) && inQueue)
        || (sharedScope && (inQueue || (!t.queueId && policy.allowWithoutQueue)));
      if (!eligible) return;
      if (!["open", "pending", "group"].includes(t.status) || (t.status === "pending" && !policy.allowPending) || (t.isGroup && !policy.allowGroups)) return;
      if (t.status === "group" && (t.whatsapp?.groupAsTicket !== "enabled" || !policy.allowGroups)) return;
      if (fresh("ticket", m)) notify("ticket", t, m, t.contact?.name || data.contact?.name || "Novo atendimento");
    };
    const onChat = data => {
      invalidate(!["new-message", "create"].includes(data?.action));
      const chat = data?.record || data?.chat, message = data?.newMessage;
      if (!summaryRef.current?.capabilities?.internalChat || data?.action !== "new-message" || !isChatParticipant(chat, userId) || Number(message?.senderId) === Number(userId)) return;
      if (chat.companyId && Number(chat.companyId) !== Number(companyId)) return;
      if (fresh("chat", message)) notify("chat", chat, message, chat.title || "Chat interno");
    };
    const onTicket = data => {
      const t = data?.ticket, policy = summaryRef.current?.ticketPolicy;
      let removedFromScope = false;
      if (t && policy) {
        const inQueue = policy.queueIds.map(Number).includes(Number(t.queueId));
        const assigned = Number(t.userId) === Number(userId) && inQueue;
        const shared = ((t.status === "pending" && policy.allowPending) || t.status === "group") && (inQueue || (!t.queueId && policy.allowWithoutQueue));
        removedFromScope = ["closed", "lgpd", "nps"].includes(t.status) || (t.isGroup && !policy.allowGroups) || (!assigned && !shared);
      }
      invalidate(["delete", "updateUnread"].includes(data?.action) || removedFromScope);
      if (["delete", "updateUnread"].includes(data?.action)) { if (desktop.has(`ticket:${data.ticketId}`)) desktop.get(`ticket:${data.ticketId}`).close(); desktop.delete(`ticket:${data.ticketId}`); }
    };
    const connected = () => { if (typeof socket.emit === "function") socket.emit("joinNotification"); refresh(); };
    const channels = [
      ["connect", connected], [`company-${companyId}-ticket`, onTicket],
      [`company-${companyId}-appMessage`, onMessage], ["company-announcement", invalidate],
      [`company-${companyId}-chat`, onChat], [`company-${companyId}-chat-user-${userId}`, onChat],
      ["notification-summary-changed", invalidate]
    ];
    channels.forEach(([name, handler]) => socket.on(name, handler));
    if (socket.connected) connected();
    return () => { if (typeof socket.off === "function") channels.forEach(([name, handler]) => socket.off(name, handler)); desktop.forEach(n => n.close()); };
  }, [socket, userId, companyId, invalidate, refresh, history]);
  return { summary, summaryError, lists, activate, load, refresh, changeReadFilter, markRead };
}
